import { type Meta } from '../types/docs';
import { WORKSPACE_AGENT_ANALYTICS_CATEGORY_META_PARAM } from './workspaceAgentAnalytics';

export const WORKSPACE_AGENT_ELICITATION_REQUESTED_TYPES = [
  'tool_consent',
  'binary_choice',
  'single_select',
  'multi_select',
  'describe',
  'text_fields',
  'stepper',
  'search_select',
  'guided_flow',
] as const;

/** Where a guided flow was opened. Set only when elicitation_type is guided_flow. */
export const WORKSPACE_AGENT_GUIDED_FLOW_ENTRIES = [
  'zero_state',
  'uploaded_batch',
  'canceled_restart',
  'actions_menu',
] as const;

export type WorkspaceAgentGuidedFlowEntry = (typeof WORKSPACE_AGENT_GUIDED_FLOW_ENTRIES)[number];

/** Names from the elicitation spec. `binary_choice` is `two_options`, `stepper` is `numeric_stepper`, `search_select` is `searchable_dropdown`. */
export const WORKSPACE_AGENT_ELICITATION_COMPONENT_TYPES = [
  'two_options',
  'single_select',
  'multi_select',
  'describe',
  'text_fields',
  'numeric_stepper',
  'searchable_dropdown',
  'tool_consent',
  'guided_flow',
] as const;

export type WorkspaceAgentElicitationComponentType = (typeof WORKSPACE_AGENT_ELICITATION_COMPONENT_TYPES)[number];

export type WorkspaceAgentElicitationRequestedType = (typeof WORKSPACE_AGENT_ELICITATION_REQUESTED_TYPES)[number];

const META: Meta = {
  description:
    'The workspace agent presents an elicitation dock, or opens a guided flow. Ask docks and tool-consent docks both fire this when the dock is shown. A guided flow fires this when the wizard opens.',
  name: 'agent_elicitation_requested',
  params: [
    WORKSPACE_AGENT_ANALYTICS_CATEGORY_META_PARAM,
    {
      description:
        'The pending elicitation id. Joins this event to agent_elicitation_responded. Empty for a guided flow, which has no elicitation row.',
      name: 'elicitation_id',
      required: true,
      type: 'string',
    },
    {
      description:
        'The agent thread (session id). Empty when a guided flow opens before a thread exists, which is the zero-state and onboarding case.',
      name: 'chat_id',
      required: true,
      type: 'string',
    },
    {
      description:
        'How the frontend classified the pending elicitation row. guided_flow is the guided variants wizard, not a dock.',
      name: 'elicitation_type',
      required: true,
      type: 'enum',
      values: [...WORKSPACE_AGENT_ELICITATION_REQUESTED_TYPES],
    },
    {
      description:
        'Spec name for the dock. Ask types use the spec enum; approval docks are tool_consent. Guided flows are guided_flow.',
      name: 'component_type',
      required: true,
      type: 'enum',
      values: [...WORKSPACE_AGENT_ELICITATION_COMPONENT_TYPES],
    },
    {
      description: '1-based position in a chained flow. Standalone docks are 1.',
      name: 'step_index',
      required: true,
      type: 'number',
    },
    {
      description: 'Whether Something else is one of the options',
      name: 'has_escape_hatch',
      required: true,
      type: 'boolean',
    },
    {
      description: 'How many options the dock offered, for choice and search lists',
      name: 'option_count',
      required: false,
      type: 'number',
    },
    {
      description: 'How many labeled inputs a text-fields dock offered',
      name: 'field_count',
      required: false,
      type: 'number',
    },
    {
      description: 'The mutating tool name when elicitation_type is tool_consent',
      name: 'tool_name',
      required: false,
      type: 'string',
    },
    {
      description:
        'Where a guided flow was opened. Only set when elicitation_type is guided_flow: the empty-thread zero state, an uploaded-batch suggestion, the canceled-note restart, or the Actions menu.',
      name: 'entry',
      required: false,
      type: 'enum',
      values: [...WORKSPACE_AGENT_GUIDED_FLOW_ENTRIES],
    },
  ],
};

export default META;
